
import VueTimepickerCustomised from '@/components/inputs/VueTimepickerCustomised.vue';
import { dateTimeFormats } from '@/lang/dateTimeFormats';
import { Icon } from '@/lib/enum/Icon';
import store from '@/store';
import { isTouchDevice, viewport } from '@/util/windowFunctions';
import spacetime from 'spacetime';
import { PropType } from 'vue';
import VueI18n from 'vue-i18n';
import TranslateResult = VueI18n.TranslateResult;

enum OutputFormats {
  String = 'string',
  Date = 'date',
}

enum ToggleStatus {
  Open = 'open',
  Close = 'close',
}

export default {
  name: 'TimePicker',

  components: {
    VueTimepickerCustomised,
  },

  props: {
    value: {
      type: [Object, Date, String, null],
      default: '',
    },

    labelText: {
      type: [String, TranslateResult],
      default: '',
    },

    placeholder: {
      type: String,
      default: '',
    },

    error: {
      type: [Array, String],
      default: null,
    },

    required: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    readOnly: {
      type: Boolean,
      default: false,
    },

    intervals: {
      // Intervals in mins
      type: Number,
      default: 15,
    },

    useCloseButton: {
      type: Boolean,
      default: false,
    },

    lazy: {
      type: Boolean,
      default: true,
    },

    menuPosition: {
      type: String,
      default: 'bottom',
      validator: (p) => ['top', 'bottom'].includes(p),
    },
    outputFormat: {
      type: String as PropType<OutputFormats>,
      default: OutputFormats.String,
    },
    anchorDate: {
      type: Date,
      default: () => new Date(),
    },

    fixedWidth: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      Icon,
      viewport,
      ToggleStatus,
      open: false,
      isTouchDevice,
    };
  },

  computed: {
    timeFormat: () => store.getters['settings/timeFormat'],
    timezone: () => store.getters.timezone,

    hasError() {
      return this.errors.length > 0;
    },

    errors() {
      return typeof this.error === 'string' ? [this.error] : this.error || [];
    },

    inputFormat() {
      return Number(this.timeFormat) === 24 ? 'HH:mm' : 'h:mm a';
    },

    showClose() {
      return this.useCloseButton && this.open && !this.viewport.md;
    },

    preparedValue() {
      return this.value instanceof Date
        ? this.$d(this.value, dateTimeFormats.hourMinute)
        : this.value ?? '';
    },
  },

  methods: {
    handleInput(time) {
      let timeString;
      if (typeof time !== 'string') {
        timeString =
          Number(this.timeFormat) === 24
            ? `${time.HH}:${time.mm}`
            : `${time.h}:${time.mm} ${time.a}`;
      } else {
        timeString = time;
      }

      if (
        (this.value instanceof Date || this.value === null) &&
        this.outputFormat === OutputFormats.Date
      ) {
        if (!timeString) {
          this.$emit('input', null);
          return;
        }

        const date = spacetime(this.anchorDate ?? this.value, this.timezone)
          .time(timeString)
          .toNativeDate();
        this.$emit('input', date);
        return;
      }

      this.$emit('input', timeString);
    },

    toggle(status: ToggleStatus) {
      this.$emit(
        status === ToggleStatus.Open ? ToggleStatus.Open : ToggleStatus.Close,
      );
      if (status === 'close') this.$emit('blur');
      this.open = status === ToggleStatus.Open;
    },
  },
};
